import React from 'react';
import {
  DeleteIcon,
  FirstName,
  LastName,
  RoleLabel,
  UserRow,
  SurfaceRolesDropdown,
  DeleteRoleLabel,
} from './style';
import { Column, HorizontalGap, Row, WithMargin } from 'components/layout';
import BarsLoader from 'components/bars-loader';
import UserAvatar from 'components/user-avatar';
import { Role } from 'components/canvas/components/top-left-menu/components/share-surface-button/components/share-surface-modal/components/email-autocomplete/components/surface-roles-dropdown';
import { useTranslation } from 'react-i18next';
import useUsersWithAccess from 'components/canvas/components/top-left-menu/components/share-surface-button/components/share-surface-modal/hooks/use-users-with-access';
import useCurrentSurface from 'routes/surface/hooks/use-current-surface';
import changeUserAccessOnSurface from 'graphql/user/mutations/change-user-access-on-surface';

import { User } from 'graphql/types';
import toast from 'services/toast';
import { useLoggedUser } from 'state/user';
import If from 'components/if-statement';
import useSurfaceRolesMap from 'routes/surface/hooks/use-surface-roles-map';

export interface UserAccessListProps {
  revokeAccess: (input: { user: string; invitation: string }) => Function;
}

const defaultProps = {};

function UserAccessList(props: UserAccessListProps) {
  const surface = useCurrentSurface();
  const { t } = useTranslation();

  const [loggedUser] = useLoggedUser();

  const surfaceRoleMap = useSurfaceRolesMap();

  const { users, isLoading, removeUserByEmail } = useUsersWithAccess(surface);

  async function changeSurfaceRoleForUser({
    role,
    user,
  }: {
    role: Role;
    user: User;
  }) {
    const usersAccess = [{ action: 'CHANGE', role: role.value, user: user.id }];

    const response = await changeUserAccessOnSurface({
      input: { usersAccess, surface: surface.id },
    });

    if (response.isSuccess) {
      return toast.success(
        t('Successfully changed role for {{user}} to {{role}}', {
          replace: { user: user.fullName, role: role.label },
        }),
      );
    }

    toast.error(response.getErrorMessage());
  }

  if (isLoading) {
    return <BarsLoader />;
  }

  return (
    <>
      {users?.map(({ user, invitation, role, isOwner }) => {
        const fullName = user?.firstName + ' ' + user?.lastName;
        const email = user?.email ?? invitation?.email;
        const label = user?.firstName ? fullName : t('Invitation pending for:');

        const isLoggedUser = user?.email === loggedUser?.email;
        const isOwnerOrLoggedUser = isLoggedUser || isOwner;

        const avatarData = user ?? { firstName: email };

        function revokeUserRole() {
          props.revokeAccess({
            user: { ...user, role },
            invitation,
          })();

          removeUserByEmail(user.email);
        }

        return (
          <UserRow spaceBetween key={email}>
            <Row alignCenter>
              <UserAvatar user={avatarData} />
              <HorizontalGap gap={16} />
              <Column>
                <FirstName>
                  {label} {isLoggedUser ? t('(You)') : ''}
                </FirstName>
                <LastName>{email}</LastName>
              </Column>
            </Row>
            <Row alignCenter>
              <If condition={isOwnerOrLoggedUser}>
                <If.Then>
                  <RoleLabel>
                    {isOwner ? t('Owner') : surfaceRoleMap[role]}
                  </RoleLabel>
                </If.Then>
                <If.Else>
                  <SurfaceRolesDropdown
                    value={role}
                    OptionWrapper={function Wrapper({ children }) {
                      return <RoleLabel withCursor>{children}</RoleLabel>;
                    }}
                    onChange={(role) =>
                      changeSurfaceRoleForUser({ role, user })
                    }
                  />
                </If.Else>
              </If>
              {isOwner === undefined && (
                <WithMargin left="16px">
                  <DeleteRoleLabel onClick={revokeUserRole} withCursor>
                    <DeleteIcon />
                  </DeleteRoleLabel>
                </WithMargin>
              )}
            </Row>
          </UserRow>
        );
      })}
    </>
  );
}

UserAccessList.defaultProps = defaultProps;

export default UserAccessList;
